/**
 * Tracking utilities.
 *
 * Two sinks, deliberately:
 *
 *  1. **Google Tag Manager** (`window.dataLayer`) — GTM forwards to Google Ads,
 *     Twitter, etc. Everything that existed in this file before went here.
 *  2. **ORTEX's own `/api/tracking/event`** — the `tracking_*` tables behind the
 *     sales reports. GTM also posts to that endpoint via a custom HTML tag, but
 *     that tag lives in the GTM container (GTM-PFFVSDC), not in this repo, and
 *     only fires for the triggers configured there. Anything we need to rely on
 *     — upgrade CTA clicks especially — is posted directly so it cannot be
 *     silently switched off from a console we do not version.
 *
 * Every function here is fire-and-forget. Tracking must never throw into a click
 * handler, never be awaited, and never stop a user reaching the payment form:
 * each sink is individually try/caught and failures are swallowed.
 */

import { oaiqPageView, oaiqRegistrationCompleted } from "@/lib/openaiPixel";
import { captureLandingUtm, getLandingUtm } from "@/lib/landingAttribution";

declare global {
    interface Window {
        dataLayer?: any[];
    }
}

const pushToDataLayer = (data: Record<string, any>) => {
    try {
        if (typeof window !== "undefined") {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(data);
        }
    } catch (err) {
        console.error("Error pushing to dataLayer:", err);
    }
};

/**
 * Current user id, kept in a module-level slot rather than read from Recoil.
 *
 * Tracking is called from plain click handlers all over the tree, so a hook-based
 * read is not available everywhere. `_app` pushes the id here whenever auth state
 * changes; a null id just means the event lands anonymously and is stitched to the
 * user later by the backend's client_id backfill.
 */
let currentUserId: number | null = null;

export const setTrackingUserId = (userId?: number | null) => {
    currentUserId = userId ?? null;
};

/**
 * Read a cookie by name, or "" when absent / unavailable (SSR, blocked storage).
 */
const readCookie = (name: string): string => {
    try {
        if (typeof document === "undefined") return "";
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const match = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"));
        return match ? decodeURIComponent(match[1]) : "";
    } catch {
        return "";
    }
};

const CID_COOKIE = "_ortex_cid";
const TWO_YEARS = 60 * 60 * 24 * 730;
// OpenAI (ChatGPT) Ads click id, landed as `?oppref=...`. Kept in our own cookie so a
// sign-up days later on app.ortex.com still carries it; the OpenAI pixel keeps its own
// copy (`__oppref`) but only after advertising consent.
const OPPREF_COOKIE = "_ortex_oppref";
const THIRTY_DAYS = 60 * 60 * 24 * 30;
// Matches the backend serializer's max_length for oppref/obref.
const CLICK_ID_MAX = 512;

/**
 * Write a first-party cookie shared across *.ortex.com (same attributes as
 * ortex-public's tracker). Off ortex.com (localhost, Vercel previews) the Domain
 * attribute is omitted so the cookie still sets on the current host.
 */
const writeCookie = (name: string, value: string, maxAge: number) => {
    try {
        const domain = window.location.hostname.endsWith("ortex.com") ? "; Domain=.ortex.com" : "";
        const secure = window.location.protocol === "https:" ? "; Secure" : "";
        document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${domain}${secure}`;
    } catch {
        /* blocked storage: the value is still sent on this event */
    }
};

const urlParam = (name: string): string => {
    try {
        return new URLSearchParams(window.location.search).get(name) || "";
    } catch {
        return "";
    }
};

const randomId = (): string => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`);

/**
 * Visitor id: `_ortex_cid` -> `_ga` (`GA1.1.<a>.<b>` -> `<a>.<b>`) -> fresh random,
 * persisted as `_ortex_cid`.
 *
 * The backend keys `TrackingVisitor` on this value. `_ortex_cid` is the id the GTM tag
 * and ortex-public already send (seeded from `_ga` when that exists, so for most
 * visitors the two are identical); preferring it here stops app.ortex.com events from
 * landing on a different visitor row than the same person's public-site journey.
 */
const getClientId = (): string => {
    let cid = readCookie(CID_COOKIE);
    if (!cid) {
        const parts = readCookie("_ga").split(".");
        cid = parts.length >= 4 ? parts[2] + "." + parts[3] : randomId();
        writeCookie(CID_COOKIE, cid, TWO_YEARS);
    }
    return cid;
};

/**
 * OpenAI Ads click id: URL `?oppref=` -> our `_ortex_oppref` cookie -> the pixel's
 * `__oppref` cookie. A URL value is persisted for 30 days. Like gclid on ortex-public,
 * this is first-party attribution and is captured regardless of ad consent.
 */
const getOppref = (): string => {
    const fromUrl = urlParam("oppref").slice(0, CLICK_ID_MAX);
    if (fromUrl) {
        if (readCookie(OPPREF_COOKIE) !== fromUrl) writeCookie(OPPREF_COOKIE, fromUrl, THIRTY_DAYS);
        return fromUrl;
    }
    return (readCookie(OPPREF_COOKIE) || readCookie("__oppref")).slice(0, CLICK_ID_MAX);
};

/** OpenAI pixel browser reference (`__obref`), set by the pixel once it has loaded. */
const getObref = (): string => readCookie("__obref").slice(0, CLICK_ID_MAX);

/**
 * Run on app load and every route change: persists an inbound `?oppref=` so it
 * survives the login / sign-up redirects, and measures the OpenAI `page_viewed`
 * (which stays queued until CookieYes advertising consent - see openaiPixel.ts).
 */
export const trackRouteView = () => {
    try {
        // Embedded widgets (/iframes/*) run on third-party pages: not our visitor's
        // landing, and no place for an ad pixel.
        if (typeof window === "undefined" || window.self !== window.top) return;
        getOppref();
        getClientId();
        captureLandingUtm();
        oaiqPageView();
    } catch {
        /* never break navigation */
    }
};

/**
 * GA4 session id from the `_ga_<MEASUREMENT_ID>` cookie (`GS1.1.<session_id>...`).
 *
 * Scans for the first `_ga_`-prefixed cookie rather than hardcoding the
 * measurement id, which is configured in GTM and not here.
 */
const getGaSessionId = (): string => {
    try {
        if (typeof document === "undefined") return "";
        const entry = document.cookie.split("; ").find((c) => c.startsWith("_ga_"));
        if (!entry) return "";
        const parts = entry.split("=")[1]?.split(".") ?? [];
        return parts.length >= 3 ? parts[2] : "";
    } catch {
        return "";
    }
};

/**
 * POST an event to ORTEX's tracking endpoint.
 *
 * Uses `fetch` with `keepalive` so the request survives the modal transition or
 * navigation the click usually triggers. Deliberately not axios: axios carries the
 * app's auth interceptors, `withCredentials` and default params, and a 401 refresh
 * storm set off by an analytics ping is exactly the failure mode this must not
 * have. The endpoint is public, CSRF-exempt and always answers 204.
 */
const postTrackingEvent = (eventName: string, eventLocation?: string, properties: Record<string, any> = {}, userId: number | null = currentUserId) => {
    try {
        if (typeof window === "undefined" || typeof fetch !== "function") return;
        const base = process.env.NEXT_PUBLIC_API_URL;
        if (!base) return;

        const body = {
            client_id: getClientId(),
            event_name: eventName,
            event_location: eventLocation || "",
            user_id: userId,
            page_url: window.location.href,
            page_title: document.title,
            referrer: document.referrer,
            session_id: getGaSessionId(),
            screen_resolution: window.screen ? window.screen.width + "x" + window.screen.height : "",
            gclid: urlParam("gclid"),
            fbclid: urlParam("fbclid"),
            msclkid: urlParam("msclkid"),
            // The session's landing utm tags (the serializer has accepted these
            // all along; nothing sent them). Kept after the first client-side
            // navigation drops them from page_url.
            ...getLandingUtm(),
            oppref: getOppref(),
            obref: getObref(),
            properties,
        };

        void fetch(base.replace(/\/$/, "") + "/api/tracking/event", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
            keepalive: true,
            // No cookies: the endpoint is anonymous, and sending credentials would
            // force the stricter CORS path for no benefit.
            credentials: "omit",
            mode: "cors",
        }).catch(() => {
            /* analytics must never surface an error to the user */
        });
    } catch {
        /* ditto — a malformed environment must not break the click */
    }
};

/**
 * Track API plans page visit
 */
export const trackApiPageVisit = (userId?: number) => {
    pushToDataLayer({ event: "api_page_view", user_id: userId ?? null });
};

/**
 * Track API purchase conversion
 * @param value - Purchase amount (defaults to 60.0)
 * @param currency - Currency code (defaults to 'GBP')
 * @param transactionId - Optional transaction ID
 * @param userId - Optional user ID
 */
export const trackApiPurchase = ({ value = 60.0, currency = "GBP", transactionId, userId }: { value?: number; currency?: string; transactionId?: string; userId?: number } = {}) => {
    pushToDataLayer({
        event: "api_purchase",
        value,
        currency,
        transaction_id: transactionId || "",
        user_id: userId ?? null,
    });
};

export const trackMIEvent = (event: string, props: Record<string, any> = {}) => pushToDataLayer({ event, ...props });

/**
 * Every place a user can start an upgrade.
 *
 * The value is written verbatim into `tracking_trackingeventlocation.name` and is
 * what the "Upgrade CTA clicks by location" table on the sales funnel report
 * groups by, so treat these as stable identifiers: renaming one splits its history
 * into two rows. Add new entries here rather than passing loose strings at the call
 * site, so the set of CTAs stays enumerable from one place.
 */
export const UPGRADE_LOCATIONS = {
    HEADER: "header",
    USER_MENU: "user_menu",
    SUBSCRIPTION_BANNER: "subscription_banner",
    OBFUSCATION_BANNER: "obfuscation_banner",
    SMALL_OBFUSCATION_BANNER: "small_obfuscation_banner",
    OBFUSCATED_FILTERS_DIALOG: "obfuscated_filters_dialog",
    DATA_GRID_CELL: "data_grid_cell",
    TABLE_CELL: "table_cell",
    DESKTOP_STOCK_STAMPS: "desktop_stock_stamps",
    DESKTOP_STOCK_STAMPS_VIRTUALIZED: "desktop_stock_stamps_virtualized",
    MOBILE_STOCK_STAMPS: "mobile_stock_stamps",
    EVENTS_TABLE: "events_table",
    INDEX_REBAL_UNLOCK_DIALOG: "index_rebal_unlock_dialog",
    OPTIONS_PLATFORM_UNLOCK_DIALOG: "options_platform_unlock_dialog",
    ORDER_BOOK: "order_book",
    ORDER_BOOK_CHART: "order_book_chart",
    BACKTESTER_RUN_BLOCK: "backtester_run_block",
    MI_LOCKED_ARTICLE: "mi_locked_article",
    SECTOR_SCORES_PAYWALL: "sector_scores_paywall",
    STOCK_SHORTS_DAILY_INTEREST: "stock_shorts_daily_interest",
    CHART_UPGRADE_PLAN_BANNER: "chart_upgrade_plan_banner",
    PAYMENT_DRAWER: "payment_drawer",
    ALT_DATA_UPGRADE_DIALOG: "alt_data_upgrade_dialog",
    DIVIDENDS_UNLOCK_DIALOG: "dividends_unlock_dialog",
    LOCKED_PANEL: "locked_panel",
    BONDS_LOCKED: "bonds_locked",
    STOCK_REPORT_LOCKED: "stock_report_locked",
    MI_STOCK_HUB_REPORT: "mi_stock_hub_report",
    STOCK_SCORES_LOCKED: "stock_scores_locked",
    SQUEEZE_STAMP: "squeeze_stamp",
    STOCK_CHART_SHORT_INTEREST: "stock_chart_short_interest",
    CHART_SHARE_SIGNUP: "chart_share_signup",
    INSIDER_PEOPLE: "insider_people",
    US_POLITICIANS: "us_politicians",
    MORNING_NOTE_HIGHLIGHTS: "morning_note_highlights",
    TRANSCRIPT_ASK: "transcript_ask",
    SQUEEZE_SCORE_PANEL: "squeeze_score_panel",
    NEWS_DETAIL: "news_detail",
    CUSTOM_VIEWS_PALETTE: "custom_views_palette",
    PORTFOLIO_BROKER_LINK: "portfolio_broker_link",
} as const;

export type UpgradeLocation = (typeof UPGRADE_LOCATIONS)[keyof typeof UPGRADE_LOCATIONS];

/**
 * Record a click on an upgrade / subscribe call-to-action.
 *
 * Opening the payment modal is pure client state — no route change, no network
 * call of its own — so without this the click is invisible to both GA and the
 * `tracking_*` tables, and we cannot tell a CTA nobody presses from one that is
 * merely never rendered.
 *
 * Call it at the click site, before handing off to the upgrade flow. It is
 * synchronous and non-blocking, so ordering against that flow does not matter.
 *
 * @param location - which CTA was pressed; use a `UPGRADE_LOCATIONS` value.
 * @param props - optional extras (current plan, ticker, gated feature ...) stored
 *                in the event's `properties` JSON.
 */
export const trackUpgradeClick = (location: UpgradeLocation, props: Record<string, any> = {}) => {
    pushToDataLayer({ event: "upgrade_click", upgrade_location: location, user_id: currentUserId, ...props });
    postTrackingEvent("upgrade_click", location, props);
};

/**
 * The signed-out "what Advanced unlocks here" panel (LockedValuePanel).
 *
 * `view` fires once per page view the panel is shown on, so click-through can be
 * read straight from the tracking tables (clicks / views per page type). Clicks
 * also go out as a normal `upgrade_click` with location `locked_panel`, so the
 * existing upgrade-click reports pick them up without a change.
 *
 * @param action - "view", or the CTA pressed ("first_month_offer", "start_free",
 *                 "tour", "collapse", "expand").
 * @param props - page type, ticker, currency, offer shown.
 */
export const trackLockedPanel = (action: string, props: Record<string, any> = {}) => {
    const payload = { ...props, action };
    if (action === "first_month_offer" || action === "start_free") {
        trackUpgradeClick(UPGRADE_LOCATIONS.LOCKED_PANEL, payload);
        return;
    }
    pushToDataLayer({ event: `locked_panel_${action}`, user_id: currentUserId, ...props });
    postTrackingEvent(`locked_panel_${action}`, UPGRADE_LOCATIONS.LOCKED_PANEL, payload);
};

/**
 * A brand-new account has just signed in for the first time.
 *
 * Three sinks:
 *  - dataLayer `sign_up` (as before), now also carrying `oppref` / `obref` so the GTM
 *    tag can forward them;
 *  - a direct `sign_up_attribution` post to `/api/tracking/event`. Named apart from
 *    `sign_up` on purpose: GTM already posts `sign_up` there and the sales reports count
 *    it, so reusing the name would double count. This event exists to bind
 *    user_id + client_id + oppref/obref on the backend (it links the visitor to the
 *    user) even when GTM's own tag does not fire;
 *  - OpenAI `registration_completed` with `event_id: signup_<userId>` (consent-gated).
 */
export const trackSignUp = (userId?: number | null) => {
    const id = userId ?? currentUserId;
    pushToDataLayer({ event: "sign_up", value: 1, currency: "USD", user_id: id ?? null, oppref: getOppref(), obref: getObref() });
    postTrackingEvent("sign_up_attribution", "", {}, id ?? null);
    oaiqRegistrationCompleted(id);
};
